import { remapAggregateCreateDimensionUnits } from '../request/dimensionUnitChoices';
import { storage } from './storage';
import { PreferencesService } from './preferencesService';
import { Request, CreateRequestData, UpdateRequestData } from '../types';
import { PartnerContributorWithAccess } from '../types';
import { CoolingSystemConfiguration } from '../types/coolingSystem';
import { LargeUpsConfiguration } from '../types/largeUps';
import { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import { LocationTag } from '../types/locationTag';
import { PowerDistributionConfiguration } from '../types/powerDistribution';
import { RackConfiguration } from '../types/rack';
import { RequestDeliverable } from '../types/requestDeliverables';
import { SmallUpsConfiguration } from '../types/smallUps';
import { UserProfile } from '../types/user';

import type { FieldPermissionMatrix } from '../types/fieldPermissions';

// Storage keys
const STORAGE_KEYS = {
  CACHED_REQUESTS: 'cached_requests',
  OFFLINE_QUEUE: 'offline_queue',
  OFFLINE_UPDATE_QUEUE: 'offline_update_queue',
  LAST_SYNC: 'last_sync',
  OFFLINE_ID_COUNTER: 'offline_id_counter',
  CACHED_PROFILE: 'cached_profile',
  CACHED_FIELD_PERMISSIONS: 'cached_field_permissions',
  CACHED_ROLE_PERMISSIONS: 'cached_role_permissions',
  OFFLINE_PROFILE_QUEUE: 'offline_profile_queue',
  OFFLINE_RACK_QUEUE: 'offline_rack_queue',
  OFFLINE_SMALL_UPS_QUEUE: 'offline_small_ups_queue',
  OFFLINE_COOLING_SYSTEM_QUEUE: 'offline_cooling_system_queue',
  OFFLINE_LIQUID_COOLING_CDU_QUEUE: 'offline_liquid_cooling_cdu_queue',
  OFFLINE_LARGE_UPS_QUEUE: 'offline_large_ups_queue',
  OFFLINE_POWER_DISTRIBUTION_QUEUE: 'offline_power_distribution_queue',
  OFFLINE_LOCATION_TAG_QUEUE: 'offline_location_tag_queue',
  CACHED_LOCATION_TAGS: 'cached_location_tags',
  OFFLINE_READ_QUEUE: 'offline_read_queue',
} as const;

// Configuration
const MAX_CACHED_REQUESTS = 50; // Keep last 50 requests cached

/**
 * Per-viewer fields that must never be served from a device-global cache.
 *
 * See `getCachedRequests` for why. Keep this list to fields whose value depends
 * on WHO is asking; a fact about the request itself belongs in the cache.
 */
const stripViewerScopedFields = (request: Request): Request => {
  // Destructured rather than looped-and-deleted: the names are written once,
  // the compiler checks them against `Request`, and there is no computed member
  // access for `security/detect-object-injection` to flag.
  if (
    !('hasDiscussion' in request) &&
    !('hasUnread' in request) &&
    // #1575 FR-5: `hasUnseenChanges` is the same kind of fact — whether THIS
    // viewer has seen the change — so it leaves on the same read. Dropping it
    // also gives the row the only honest offline state: absent, which the list
    // renders as read rather than bolding one person's unread for another.
    !('hasUnseenChanges' in request)
  ) {
    // Untouched rows keep their identity, so a cache read allocates nothing
    // when there is nothing to strip (the common case, once a device has
    // replaced what it cached before this fix).
    return request;
  }
  const {
    hasDiscussion: _hasDiscussion,
    hasUnread: _hasUnread,
    hasUnseenChanges: _hasUnseenChanges,
    ...withoutViewerScopedFields
  } = request;
  return withoutViewerScopedFields;
};

/**
 * The team sub-object carried by both queues.
 *
 * `partnerContributors` keeps the `{ email, accessLevel }` objects rather than bare emails:
 * the replay hands this straight to `saveTeamMembersFromForm`, and a list reduced to emails
 * comes back with every partner defaulted to `read`, silently demoting a `read_write` one.
 *
 * `useDefaultSponsors` and `region` ride along for the same reason — `getSponsorRole` needs
 * both to resolve `default_sponsor_nam/emea/apac`, and without them the replay always falls
 * back to plain `vertiv_sponsor`, which is a different role from the one the online path
 * would have written.
 */
interface QueuedTeamMembers {
  vertivSponsors?: string[];
  vertivContributors?: string[];
  partnerContributors?: Array<string | PartnerContributorWithAccess>;
  useDefaultSponsors?: boolean;
  region?: string;
}

interface OfflineRequest {
  id: string; // Temporary offline ID
  data: CreateRequestData;
  teamMembers?: QueuedTeamMembers;
  /**
   * Deliverables ride the request's own queue as a merged sub-object rather than a queue
   * of their own (spec C2). The stored value is the **whole intended list**, replayed as
   * one batch PUT — never a diff — so an empty array is a real value meaning "clear it",
   * and `undefined` means "the caller said nothing about deliverables".
   */
  deliverables?: RequestDeliverable[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

interface OfflineUpdate {
  requestId: string; // The actual request ID being updated
  data: UpdateRequestData;
  teamMembers?: QueuedTeamMembers;
  /** Whole intended list. See `OfflineRequest.deliverables`. */
  deliverables?: RequestDeliverable[];
  /**
   * What the device could prove the server held when this edit was made, sent as the `base`
   * on replay so the server will allow the removals the user actually intended.
   *
   * Set once and never overwritten: a second offline edit supersedes `deliverables` but not
   * this, because the server has not moved between the two and the original proof is still
   * the true one. Absent on an entry queued before this existed, which reads as an empty
   * proof and so permits additions and comment edits but no deletions.
   * (MR !489 review, batch 1b change 1.5.)
   */
  deliverablesBase?: RequestDeliverable[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

interface CacheMetadata {
  lastUpdated: number;
  totalCached: number;
}

/**
 * Offline rack operation for queuing CRUD operations while offline
 * @see GitLab #995 — Multiple Rack Instances
 */
/**
 * #1501 — ambient offline save-group window. One user Save (RequestDetail /
 * CreateRequest offline paths) brackets its queueing with begin/end; every
 * add*ToOfflineQueue call inside the window stamps the same saveGroupId.
 * Module-level is safe for the same reason the Option A operation window was:
 * the save orchestrators await their queueing sequentially. Always end in a
 * `finally` so the group can never leak onto later unrelated queueing.
 */
let currentOfflineSaveGroupId: string | null = null;

const currentSaveGroupStamp = (): { saveGroupId?: string } =>
  currentOfflineSaveGroupId ? { saveGroupId: currentOfflineSaveGroupId } : {};

export interface OfflineRackOperation {
  id: string;
  requestId: string;
  rackId?: number;
  operation: 'create' | 'update' | 'delete' | 'reorder';
  data?: Partial<RackConfiguration>;
  rackIds?: number[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

export interface OfflineSmallUpsOperation {
  id: string;
  requestId: string;
  smallUpsId?: number;
  operation: 'create' | 'update' | 'delete' | 'reorder';
  data?: Partial<SmallUpsConfiguration>;
  smallUpsIds?: number[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

/**
 * Offline cooling system operation for queuing CRUD operations while offline
 * @see GitLab #996 — Repeating Sections (Phase 2: Cooling)
 */
export interface OfflineCoolingSystemOperation {
  id: string;
  requestId: string;
  coolingSystemId?: number;
  operation: 'create' | 'update' | 'delete' | 'reorder';
  data?: Partial<CoolingSystemConfiguration>;
  coolingSystemIds?: number[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

/**
 * Offline liquid cooling CDU operation for queuing CRUD operations while offline
 * @see GitLab #996 — Repeating Sections (Phase 3: Liquid Cooling)
 */
export interface OfflineLiquidCoolingCduOperation {
  id: string;
  requestId: string;
  liquidCoolingCduId?: number;
  operation: 'create' | 'update' | 'delete' | 'reorder';
  data?: Partial<LiquidCoolingCduConfiguration>;
  liquidCoolingCduIds?: number[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

/**
 * Offline large UPS operation for queuing CRUD operations while offline
 * @see GitLab #996 — Repeating Sections (Phase 4: Facility Power / Large UPS)
 */
export interface OfflineLargeUpsOperation {
  id: string;
  requestId: string;
  largeUpsId?: number;
  operation: 'create' | 'update' | 'delete' | 'reorder';
  data?: Partial<LargeUpsConfiguration>;
  largeUpsIds?: number[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

/**
 * Offline power distribution operation for queuing CRUD operations while offline
 * @see GitLab #996 — Repeating Sections (Facility Power / Power Distribution)
 */
export interface OfflinePowerDistributionOperation {
  id: string;
  requestId: string;
  powerDistributionId?: number;
  operation: 'create' | 'update' | 'delete' | 'reorder';
  data?: Partial<PowerDistributionConfiguration>;
  powerDistributionIds?: number[];
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

/**
 * Offline location tag operation for queuing CRUD while offline
 * @see GitLab #999 — Location tags
 */
export interface OfflineLocationTagOperation {
  id: string;
  requestId: string;
  tagId?: number;
  operation: 'create' | 'update' | 'delete';
  data?: { name: string };
  timestamp: number;
  status: 'pending' | 'syncing' | 'failed';
  /**
   * #1501: groups every op queued by ONE user Save (stamped from the ambient
   * save-group window below). The offline-sync replay can batch a group into
   * one aggregate save call — one Version History entry per original Save.
   * Optional: legacy queued ops (and non-Save writers) have none.
   */
  saveGroupId?: string;
}

/**
 * One queued "the person opened this request at `readAt`" marker (#1575 story 3.2).
 *
 * Shares the family's persistence shape — an array under one `STORAGE_KEYS` key,
 * each entry with an `id`, a `requestId` and a `timestamp` — and deliberately
 * differs from it in three ways, each for a stated reason:
 *
 * - **No `operation`.** There is one verb (`POST /requests/:id/read`); a union
 *   with a single member is noise.
 * - **No `status`.** The unit families carry `'pending' | 'syncing' | 'failed'`
 *   and only ever replay `=== 'pending'`, so an entry stranded in `'syncing'` by
 *   an app kill mid-sync is never retried. A read marker is idempotent and the
 *   server's upsert is monotonic, so every entry in this queue is always safe to
 *   replay, and AC 2 is specifically about surviving a restart — a status field
 *   here could only strand a read, never protect one. The one permanent outcome
 *   (a 4xx) **removes** the entry instead of parking it as `'failed'`.
 * - **No `saveGroupId`.** A read is not part of a user Save, so it belongs to no
 *   `#1501` save group and must never be batched into an aggregate save.
 *
 * At most ONE entry exists per `requestId`: a second read of the same request
 * collapses onto the latest `readAt` (AC 3) — the server would keep only the
 * newest marker anyway, so carrying both would be storage with no effect.
 *
 * @see GitLab #1575 — Change Notification UI
 */
export interface OfflineReadOperation {
  id: string;
  requestId: string;
  /**
   * ISO 8601 with a zone designator (`new Date().toISOString()`), the instant the
   * person OPENED the request. Replayed verbatim, never re-stamped at replay
   * time: a marker at replay time would hide every change made between the open
   * and the sync.
   */
  readAt: string;
  timestamp: number;
}

/**
 * Offline cache service for requests
 * Handles caching requests for offline viewing and queuing offline operations
 */
class OfflineCacheService {
  // Cache Management
  /**
   * Replaces the cached request list.
   *
   * `deliverables` is carried forward from the existing cached row whenever the incoming row
   * does not mention it. This is not a nicety: deliverables arrive on their own GET, never on
   * the request payload, so every caller that passes raw server rows — `getAllRequests`,
   * `getRequestById`, `refreshCache` — omits the key. Without the carry-forward, a routine
   * list refresh silently drops the base that `updateCachedRequestDeliverables` wrote, the
   * next offline read sees an empty list, and the whole-list PUT built from it deletes every
   * row the device never saw. (MR !489 review, finding 01.)
   *
   * A row that *does* carry `deliverables` wins outright: a caller speaking about them is
   * authoritative, including when it says the list is now empty.
   */
  async cacheRequests(requests: Request[]): Promise<void> {
    try {
      // Keep only the most recent requests (up to MAX_CACHED_REQUESTS)
      const requestsToCache = requests.slice(0, MAX_CACHED_REQUESTS);

      const { requests: existing } = await this.getCachedRequests();
      const deliverablesById = new Map(
        existing
          .filter(request => request.deliverables !== undefined)
          .map(request => [String(request.id), request.deliverables]),
      );

      const merged = requestsToCache.map(request => {
        if (request.deliverables !== undefined) return request;
        const carried = deliverablesById.get(String(request.id));
        return carried === undefined
          ? request
          : { ...request, deliverables: carried };
      });

      await storage.setJSON(STORAGE_KEYS.CACHED_REQUESTS, {
        requests: merged,
        metadata: {
          lastUpdated: Date.now(),
          totalCached: merged.length,
        } as CacheMetadata,
      });

      console.log(`Cached ${merged.length} requests offline`);
    } catch (error) {
      console.error('Error caching requests:', error);
    }
  }

  /**
   * Serve the cache WITHOUT the per-viewer correspondence flags (#1069 Story 2.2
   * code review, HIGH).
   *
   * `cached_requests` is a single unscoped storage key, it is never cleared on
   * logout, and `ViewRequests` falls back to it whenever the paginated page is
   * empty (which includes every cold start, because the paginated query is
   * disabled until roles load). Everything else in this cache is a fact about a
   * REQUEST; `hasDiscussion` and `hasUnread` are facts about a VIEWER, so a
   * second person signing in on the same device would read the first person's
   * answers. For an external requester that is the existence signal the whole
   * server path is built to withhold (FR69a, NFR55).
   *
   * Stripped on READ, not on write, because that is the only version that also
   * covers the rows already persisted on devices in the field.
   *
   * Nothing is lost: cached rows are served only when offline or when the list
   * fetch failed (`offlineRequestsService.getAllRequests`), and AC 11 already
   * specifies that cached rows without the flags draw nothing. Online reads
   * return the server's fresh rows and never pass through here.
   */
  async getCachedRequests(): Promise<{
    requests: Request[];
    metadata: CacheMetadata | null;
  }> {
    try {
      const cached = await storage.getJSON<{
        requests: Request[];
        metadata: CacheMetadata;
      }>(STORAGE_KEYS.CACHED_REQUESTS);

      if (!cached) {
        return { requests: [], metadata: null };
      }

      return {
        requests: (cached.requests || []).map(stripViewerScopedFields),
        metadata: cached.metadata || null,
      };
    } catch (error) {
      console.error('Error getting cached requests:', error);
      return { requests: [], metadata: null };
    }
  }

  /** Insert or replace a single request in the offline cache (Create Request save path). */
  async upsertCachedRequest(request: Request): Promise<void> {
    try {
      const { requests } = await this.getCachedRequests();
      const idKey = String(request.id);
      const next = [
        request,
        ...requests.filter(item => String(item.id) !== idKey),
      ];
      await this.cacheRequests(next);
    } catch (error) {
      console.error('Error upserting cached request:', error);
    }
  }

  async getCachedRequestById(id: string): Promise<Request | null> {
    // Pending offline creates use ids like `o-1002` and live in the offline queue only — not cached_requests.
    // We need to check the offline queue for o-* IDs and convert them to Request-like objects.
    if (id.startsWith('o-')) {
      try {
        const offlineRequest = await this.getOfflineRequestById(id);
        if (offlineRequest) {
          // Convert OfflineRequest to a Request-like object for consistent API
          const requestLike: Request = {
            id: offlineRequest.id,
            customer_name:
              offlineRequest.data.customer_name ||
              offlineRequest.data.customerName ||
              '',
            requestor_name:
              offlineRequest.data.requestor_name ||
              offlineRequest.data.requestorName ||
              '',
            region: offlineRequest.data.region || '',
            request_type:
              offlineRequest.data.request_type ||
              offlineRequest.data.requestType ||
              '',
            stage: offlineRequest.data.stage || 'Draft',
            priority: offlineRequest.data.priority || '',
            description: offlineRequest.data.description || '',
            date_created: new Date(offlineRequest.timestamp).toISOString(),
            modified_on: new Date(offlineRequest.timestamp).toISOString(),
            modified_by:
              offlineRequest.data.requestor_name ||
              offlineRequest.data.requestorName ||
              '',
            additional_details:
              offlineRequest.data.additional_details ||
              offlineRequest.data.additionalDetails ||
              '',
            // Include rack configurations if stored in the offline request
            rackConfigurations:
              (
                offlineRequest.data as unknown as {
                  rackConfigurations?: RackConfiguration[];
                }
              ).rackConfigurations || [],
            // Spread any additional fields
            ...offlineRequest.data,
          };
          console.log(`✅ Found offline request ${id} in queue`);
          return requestLike;
        }
        console.log(`❌ Offline request ${id} NOT in queue`);
        return null;
      } catch (error) {
        console.error('Error getting offline request by ID:', error);
        return null;
      }
    }
    try {
      const { requests } = await this.getCachedRequests();
      const found = requests.find(request => request.id === id);

      if (found) {
        console.log(
          `✅ Found request ${id} in cache (${requests.length} total cached)`,
        );
      } else {
        console.log(
          `❌ Request ${id} NOT in cache. Cached IDs:`,
          requests.map(r => r.id).join(', '),
        );
      }

      return found || null;
    } catch (error) {
      console.error('Error getting cached request by ID:', error);
      return null;
    }
  }

  // Offline Queue Management
  async getNextOfflineId(): Promise<string> {
    try {
      // Get the current counter value, defaulting to 999 (so first ID will be o-1000)
      const counterData = await storage.getJSON<{ counter: number }>(
        STORAGE_KEYS.OFFLINE_ID_COUNTER,
      );
      const currentCounter = counterData?.counter || 999;

      // Increment the counter
      const nextCounter = currentCounter + 1;

      // Save the new counter value
      await storage.setJSON(STORAGE_KEYS.OFFLINE_ID_COUNTER, {
        counter: nextCounter,
      });

      return `o-${nextCounter}`;
    } catch (error) {
      console.error('Error generating offline ID:', error);
      // Fallback to timestamp-based ID if counter fails
      return `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  async addToOfflineQueue(
    requestData: CreateRequestData,
    teamMembers?: QueuedTeamMembers,
    deliverables?: RequestDeliverable[],
  ): Promise<string> {
    try {
      await PreferencesService.rememberDimensionUnitDefaultsLocally(
        requestData.dimensionUnits,
      );
      const offlineId = await this.getNextOfflineId();

      const offlineRequest: OfflineRequest = {
        id: offlineId,
        data: requestData,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      // Add team members if provided
      if (
        teamMembers &&
        (teamMembers.vertivSponsors?.length ||
          teamMembers.vertivContributors?.length ||
          teamMembers.partnerContributors?.length)
      ) {
        offlineRequest.teamMembers = {
          vertivSponsors: teamMembers.vertivSponsors || [],
          vertivContributors: teamMembers.vertivContributors || [],
          partnerContributors: teamMembers.partnerContributors || [],
          // Sponsor-role resolution inputs. Only stored when the caller supplied them, so a
          // queue entry written before this existed still reads back as "no opinion".
          ...(teamMembers.useDefaultSponsors !== undefined && {
            useDefaultSponsors: teamMembers.useDefaultSponsors,
          }),
          ...(teamMembers.region !== undefined && {
            region: teamMembers.region,
          }),
        };
        console.log(
          `Storing team members with offline request:`,
          offlineRequest.teamMembers,
        );
      }

      // Gated on `!== undefined`, deliberately NOT on `.length`: an empty list is a
      // deliberate "no deliverables" and must survive the replay.
      if (deliverables !== undefined) {
        offlineRequest.deliverables = deliverables;
      }

      const queue = await this.getOfflineQueue();
      queue.push(offlineRequest);

      await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);

      console.log(`Added request to offline queue: ${offlineId}`);
      return offlineId;
    } catch (error) {
      console.error('Error adding to offline queue:', error);
      throw new Error('Failed to queue offline request');
    }
  }

  async getOfflineQueue(): Promise<OfflineRequest[]> {
    try {
      const queue = await storage.getJSON<OfflineRequest[]>(
        STORAGE_KEYS.OFFLINE_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline queue:', error);
      return [];
    }
  }

  async updateOfflineRequestStatus(
    offlineId: string,
    status: OfflineRequest['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineQueue();
      const requestIndex = queue.findIndex(req => req.id === offlineId);

      if (requestIndex !== -1) {
        queue[requestIndex].status = status;
        await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
      }
    } catch (error) {
      console.error('Error updating offline request status:', error);
    }
  }

  async removeFromOfflineQueue(offlineId: string): Promise<void> {
    try {
      const queue = await this.getOfflineQueue();
      const filteredQueue = queue.filter(req => req.id !== offlineId);
      await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, filteredQueue);

      console.log(`Removed request from offline queue: ${offlineId}`);
    } catch (error) {
      console.error('Error removing from offline queue:', error);
    }
  }

  async getPendingOfflineRequests(): Promise<OfflineRequest[]> {
    const queue = await this.getOfflineQueue();
    return queue.filter(req => req.status === 'pending');
  }

  async getFailedOfflineRequests(): Promise<OfflineRequest[]> {
    const queue = await this.getOfflineQueue();
    return queue.filter(req => req.status === 'failed');
  }

  /**
   * Get a specific offline request by its ID
   * Used for detail view of offline requests
   */
  async getOfflineRequestById(
    offlineId: string,
  ): Promise<OfflineRequest | null> {
    try {
      const queue = await this.getOfflineQueue();
      return queue.find(req => req.id === offlineId) || null;
    } catch (error) {
      console.error('Error getting offline request by ID:', error);
      return null;
    }
  }

  /**
   * Update an existing offline request in the queue
   * Used when editing offline requests before they sync
   * Implements "last write wins" - overwrites all data
   */
  async updateOfflineRequest(
    offlineId: string,
    updatedData: Partial<CreateRequestData>,
    updatedTeamMembers?: QueuedTeamMembers,
    updatedDeliverables?: RequestDeliverable[],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineQueue();
      const requestIndex = queue.findIndex(req => req.id === offlineId);

      if (requestIndex === -1) {
        throw new Error(`Offline request ${offlineId} not found`);
      }

      // Merge updated data with existing data (last write wins)
      queue[requestIndex].data = {
        ...queue[requestIndex].data,
        ...updatedData,
      };

      // Update team members if provided
      if (updatedTeamMembers) {
        const existingTeam = queue[requestIndex].teamMembers;
        // Sponsor-role inputs: keep whichever side has one, and add no key at all when
        // neither does, so an entry written before this existed still reads unchanged.
        const useDefaultSponsors =
          updatedTeamMembers.useDefaultSponsors ??
          existingTeam?.useDefaultSponsors;
        const region = updatedTeamMembers.region ?? existingTeam?.region;

        queue[requestIndex].teamMembers = {
          vertivSponsors:
            updatedTeamMembers.vertivSponsors ||
            existingTeam?.vertivSponsors ||
            [],
          vertivContributors:
            updatedTeamMembers.vertivContributors ||
            existingTeam?.vertivContributors ||
            [],
          partnerContributors:
            updatedTeamMembers.partnerContributors ||
            existingTeam?.partnerContributors ||
            [],
          ...(useDefaultSponsors !== undefined && { useDefaultSponsors }),
          ...(region !== undefined && { region }),
        };
      }

      // Last write wins on the whole list — see `OfflineRequest.deliverables`. `!==
      // undefined` so a clear-to-empty replaces the stored list rather than being ignored.
      if (updatedDeliverables !== undefined) {
        queue[requestIndex].deliverables = updatedDeliverables;
      }

      // Update timestamp to reflect latest edit
      queue[requestIndex].timestamp = Date.now();

      await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);

      console.log(
        `Updated offline request ${offlineId} in queue (last write wins)`,
      );
    } catch (error) {
      console.error('Error updating offline request:', error);
      throw error;
    }
  }

  // Sync Management
  async setLastSyncTime(timestamp: number = Date.now()): Promise<void> {
    try {
      await storage.setJSON(STORAGE_KEYS.LAST_SYNC, { timestamp });
    } catch (error) {
      console.error('Error setting last sync time:', error);
    }
  }

  async getLastSyncTime(): Promise<number | null> {
    try {
      const syncData = await storage.getJSON<{ timestamp: number }>(
        STORAGE_KEYS.LAST_SYNC,
      );
      return syncData?.timestamp || null;
    } catch (error) {
      console.error('Error getting last sync time:', error);
      return null;
    }
  }

  // Cache Statistics
  async getCacheStats(): Promise<{
    cachedRequestsCount: number;
    pendingOfflineCount: number;
    failedOfflineCount: number;
    lastSyncTime: number | null;
    lastCacheUpdate: number | null;
  }> {
    const { requests, metadata } = await this.getCachedRequests();
    const pendingRequests = await this.getPendingOfflineRequests();
    const failedRequests = await this.getFailedOfflineRequests();
    const lastSyncTime = await this.getLastSyncTime();

    return {
      cachedRequestsCount: requests.length,
      pendingOfflineCount: pendingRequests.length,
      failedOfflineCount: failedRequests.length,
      lastSyncTime,
      lastCacheUpdate: metadata?.lastUpdated || null,
    };
  }

  // Offline Update Queue Management
  async addToOfflineUpdateQueue(
    requestId: string,
    updateData: UpdateRequestData,
    teamMembers?: QueuedTeamMembers,
    deliverables?: RequestDeliverable[],
    deliverablesBase?: RequestDeliverable[],
  ): Promise<void> {
    try {
      await PreferencesService.rememberDimensionUnitDefaultsLocally(
        updateData.dimensionUnits,
      );
      const offlineUpdate: OfflineUpdate = {
        requestId,
        data: updateData,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      // Add team members if provided
      if (
        teamMembers &&
        (teamMembers.vertivSponsors !== undefined ||
          teamMembers.vertivContributors !== undefined ||
          teamMembers.partnerContributors !== undefined)
      ) {
        offlineUpdate.teamMembers = teamMembers;
        console.log(
          `Storing team member updates with offline update:`,
          offlineUpdate.teamMembers,
        );
      }

      if (deliverables !== undefined) {
        offlineUpdate.deliverables = deliverables;
        if (deliverablesBase !== undefined) {
          offlineUpdate.deliverablesBase = deliverablesBase;
        }
      }

      const queue = await this.getOfflineUpdateQueue();

      // Check if there's already a pending update for this request
      const existingIndex = queue.findIndex(
        u => u.requestId === requestId && u.status === 'pending',
      );

      if (existingIndex !== -1) {
        // Merge with existing update
        queue[existingIndex].data = {
          ...queue[existingIndex].data,
          ...updateData,
        };
        queue[existingIndex].timestamp = Date.now();

        // Merge team members if provided
        if (offlineUpdate.teamMembers) {
          queue[existingIndex].teamMembers = {
            ...queue[existingIndex].teamMembers,
            ...offlineUpdate.teamMembers,
          };
        }

        // Whole-list replace, not a spread: the queued value IS the intended final list,
        // so the second offline edit simply supersedes the first (last write wins).
        if (deliverables !== undefined) {
          queue[existingIndex].deliverables = deliverables;
          // The base is the exception, and the opposite rule: FIRST write wins. The server
          // has not moved between two offline edits, so the proof gathered before the first
          // one is still the true one, while the second edit's idea of the base is its own
          // queued list. Overwriting here would claim proof of the first edit's removals and
          // then ask the server to allow them, which is the deletion this batch refuses.
          if (
            deliverablesBase !== undefined &&
            queue[existingIndex].deliverablesBase === undefined
          ) {
            queue[existingIndex].deliverablesBase = deliverablesBase;
          }
        }
      } else {
        // Add new update
        queue.push(offlineUpdate);
      }

      await storage.setJSON(STORAGE_KEYS.OFFLINE_UPDATE_QUEUE, queue);

      // Also update the cached request immediately for offline viewing
      await this.updateCachedRequest(requestId, updateData);

      console.log(`Added request update to offline queue: ${requestId}`);
    } catch (error) {
      console.error('Error adding to offline update queue:', error);
      throw new Error('Failed to queue offline update');
    }
  }

  async getOfflineUpdateQueue(): Promise<OfflineUpdate[]> {
    try {
      const queue = await storage.getJSON<OfflineUpdate[]>(
        STORAGE_KEYS.OFFLINE_UPDATE_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline update queue:', error);
      return [];
    }
  }

  async updateCachedRequest(
    requestId: string,
    updateData: UpdateRequestData,
  ): Promise<void> {
    try {
      const { requests, metadata } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex] = { ...requests[requestIndex], ...updateData };
        await this.cacheRequests(requests);
        console.log(`Updated cached request: ${requestId}`);
      }
    } catch (error) {
      console.error('Error updating cached request:', error);
    }
  }

  /** Point a draft chip at the id sync just assigned, in the cached request. */
  async remapCachedDimensionUnits(
    requestId: string,
    family: 'racks' | 'coolingSystems' | 'powerDistribution',
    tempId: number,
    realId: number,
  ): Promise<void> {
    try {
      const { requests } = await this.getCachedRequests();
      const index = requests.findIndex(
        req => String(req.id) === String(requestId),
      );
      if (index === -1) return;
      const remapped = remapAggregateCreateDimensionUnits(
        requests[index].dimensionUnits,
        { [`${family}:${tempId}`]: realId },
      );
      requests[index] = { ...requests[index], dimensionUnits: remapped };
      await this.cacheRequests(requests);
    } catch (error) {
      console.error('Error remapping cached unit chips:', error);
    }
  }

  async removeFromOfflineUpdateQueue(requestId: string): Promise<void> {
    try {
      const queue = await this.getOfflineUpdateQueue();
      const filteredQueue = queue.filter(
        update => update.requestId !== requestId,
      );
      await storage.setJSON(STORAGE_KEYS.OFFLINE_UPDATE_QUEUE, filteredQueue);
    } catch (error) {
      console.error('Error removing from offline update queue:', error);
    }
  }

  // ──── Deliverables on the request's queues ────
  //
  // Deliverables get no queue, storage key or sync module of their own: they ride the
  // request they belong to, exactly as team members do. These methods are the whole
  // surface — queue write, queue read, cache write, cache read — and they pick the create
  // queue or the update queue from the id, so no caller has to know which one a request
  // is sitting in.

  /**
   * Queues the whole intended deliverables list against a request.
   *
   * `o-*` ids are requests that do not exist on the server yet, so their list merges into
   * the create-queue entry and is flushed once the request is created. Everything else is
   * a real server id and rides the update queue.
   *
   * Never throws, and that is deliberate. `updateOfflineRequest` does throw when its
   * create-queue entry has gone — which happens legitimately, when the create synced between
   * the user opening the screen and pressing Save — and this is called as the first statement
   * of RequestDetail's save, so an escape here would abort the whole save over a request that
   * is already on the server.
   *
   * It does, however, **report**. Swallowing the error was one thing; returning as though the
   * write had landed was another, and it cost the user their edit: the caller promoted the
   * list to `persisted` and cleared `pending`, discarding the only remaining copy. Callers
   * must branch on this result rather than assuming a queue write succeeded.
   * (MR !489 review, finding 01 / the swallow cluster.)
   *
   * `base` is what the caller could prove the server held when the edit was made. It is
   * stored beside the list and sent on replay, because by then nothing can reconstruct it:
   * the queued list is the user's `next`, and the cache may have moved on.
   *
   * @returns `true` when the list is durably queued, `false` when nothing was written.
   */
  async queueDeliverables(
    requestId: string,
    base: RequestDeliverable[],
    deliverables: RequestDeliverable[],
  ): Promise<boolean> {
    try {
      if (requestId.startsWith('o-')) {
        // No base is stored for an `o-*` request and none is needed: the request does not
        // exist on the server, so its whole list is additions and the replay sends `[]`.
        await this.updateOfflineRequest(requestId, {}, undefined, deliverables);
        return true;
      }
      await this.addToOfflineUpdateQueue(
        requestId,
        {},
        undefined,
        deliverables,
        base,
      );
      return true;
    } catch (error) {
      console.error(
        `Error queuing deliverables for request ${requestId}:`,
        error,
      );
      return false;
    }
  }

  /**
   * Store the deliverables list on the cached request, so an offline read has a real base
   * to edit from rather than an empty one.
   *
   * Mirrors `updateCachedRequestRacks`, including the `o-*` branch, because deliverables
   * have the same problem racks have and the opposite of the solution racks got: racks
   * arrive on the request payload and are cached for free, deliverables have their own GET
   * and so must be written here explicitly. Without this an offline reader sees `[]`, and
   * the whole-list PUT built from that base deletes every row the device never saw.
   */
  async updateCachedRequestDeliverables(
    requestId: string,
    deliverables: RequestDeliverable[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs). Their list lives on the queue entry
      // itself, which is also where `getQueuedDeliverables` reads it from — correct,
      // because nothing about an `o-*` request has reached the server yet.
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          queue[requestIndex].deliverables = deliverables;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${deliverables.length} deliverables`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update deliverables`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex === -1) {
        // Not a no-op worth hiding. This is the only mechanism that seeds a safe offline
        // base, so a miss here means the next offline edit starts from nothing — the exact
        // setup for a destructive whole-list PUT. Reachable whenever the request falls
        // outside MAX_CACHED_REQUESTS or was never listed. (MR !489 review, finding 01.)
        console.warn(
          `Request ${requestId} is not cached, so its deliverables base was not stored. ` +
            'An offline edit of this request will have no local base to build from.',
        );
        return;
      }

      requests[requestIndex].deliverables = deliverables;
      await this.cacheRequests(requests);
      console.log(
        `Updated cached request ${requestId} with ${deliverables.length} deliverables`,
      );
    } catch (error) {
      console.error('Error updating cached request deliverables:', error);
    }
  }

  /**
   * Keeps a rejected deliverables list on the queue instead of letting it be drained.
   *
   * `permanent` decides whether it is also marked `failed`, and the two cases are genuinely
   * different rather than a shade of the same one. A stale base can never succeed however
   * many times it is re-sent, so it is `failed`: retried, it would report the same error on
   * every sync forever. A dropped connection is the opposite — the only thing wrong is the
   * moment — so the entry stays `pending` and the next sync tries again on its own.
   * Collapsing them either strands a recoverable edit or spins on an unrecoverable one.
   * (MR !489 review, AC1.8.3, batch 1b.)
   *
   * The replay used to record the failure into `result.deliverableSyncFailures` and then
   * remove the whole entry two statements later, so the list was neither on the server nor
   * on the device and nothing told the user. Retaining it makes a retry possible.
   *
   * Any part of the entry that DID sync (the request `data` patch, team members) is dropped,
   * because replaying those again on retry would be a second write of work already done.
   *
   * `failed` is a real status with two real readers. `getQueuedDeliverables` filters it out,
   * so the section falls back to showing what the server actually holds rather than a list
   * the device failed to save; and the replay loop in `syncService` skips it, so a rejection
   * that can never succeed is not re-sent on every sync. Before this, nothing anywhere wrote
   * the status. The read-path filter alone was not enough — it left the replay retrying
   * forever, which only became visible once a 409 made permanent rejection possible.
   * (MR !489 review, finding 01 / the silent-replay-failure cluster, and batch 1b.)
   */
  async markQueuedDeliverablesFailed(
    requestId: string,
    permanent = true,
  ): Promise<void> {
    try {
      const queue = await this.getOfflineUpdateQueue();
      let touched = false;

      const next = queue.map(update => {
        if (
          update.requestId !== requestId ||
          update.deliverables === undefined
        ) {
          return update;
        }
        touched = true;
        return {
          ...update,
          data: {},
          teamMembers: undefined,
          status: permanent ? ('failed' as const) : update.status,
        };
      });

      if (!touched) return;

      await storage.setJSON(STORAGE_KEYS.OFFLINE_UPDATE_QUEUE, next);
      console.warn(
        `Deliverables for request ${requestId} were rejected on replay and have been kept ` +
          `on the queue${
            permanent ? ' as failed' : ' for retry'
          }. They are not on the server.`,
      );
    } catch (error) {
      console.error('Error marking queued deliverables failed:', error);
    }
  }

  /**
   * Drops just the queued deliverables for a request, leaving any other queued work alone.
   *
   * Deliberately not `removeFromOfflineUpdateQueue`, which deletes the whole entry: an update
   * entry can also carry a request `data` patch and team-member edits, and throwing those away
   * to clear a deliverables list would be the same class of silent loss this change exists to
   * fix. The entry is removed only when clearing the deliverables leaves nothing else in it.
   *
   * Called after a successful online save, so a stale queued list cannot later replay over
   * newer server state. (MR !489 review, finding 02.)
   */
  async clearQueuedDeliverables(requestId: string): Promise<void> {
    try {
      if (requestId.startsWith('o-')) {
        // An `o-*` request has no server state to have moved on, and its list is the only
        // copy there is. Nothing to reconcile, so nothing to clear.
        return;
      }

      const queue = await this.getOfflineUpdateQueue();
      let touched = false;

      const next = queue.reduce<typeof queue>((acc, update) => {
        if (
          update.requestId !== requestId ||
          update.deliverables === undefined
        ) {
          acc.push(update);
          return acc;
        }

        touched = true;
        // The base goes with the list it belonged to. Leaving it behind on an entry that
        // still carries other work would strand a proof of a save that has already landed,
        // and the next queued edit would then keep it under the first-write-wins merge.
        const {
          deliverables: _cleared,
          deliverablesBase: _clearedBase,
          ...rest
        } = update;
        const carriesOtherWork =
          (rest.data && Object.keys(rest.data).length > 0) ||
          rest.teamMembers !== undefined;

        if (carriesOtherWork) acc.push(rest as (typeof queue)[number]);
        return acc;
      }, []);

      if (!touched) return;

      await storage.setJSON(STORAGE_KEYS.OFFLINE_UPDATE_QUEUE, next);
      console.log(`Cleared queued deliverables for request ${requestId}`);
    } catch (error) {
      console.error('Error clearing queued deliverables:', error);
    }
  }

  /**
   * The deliverables held on the cached request, or `null` when the request is not cached
   * at all.
   *
   * A cached request with no `deliverables` key reads as an empty list, not as `null`: that
   * is a request cached before this shipped, and the caller's only alternative would be to
   * go to a network it has already established is absent.
   */
  async getCachedRequestDeliverables(
    requestId: string,
  ): Promise<RequestDeliverable[] | null> {
    try {
      const cached = await this.getCachedRequestById(requestId);
      if (!cached) return null;
      return cached.deliverables ?? [];
    } catch (error) {
      console.error('Error reading cached request deliverables:', error);
      return null;
    }
  }

  /**
   * The queued list for a request, or `null` when nothing is queued.
   *
   * The read path has to come here rather than to the cached request: `updateCachedRequest`
   * merges `data` only, so a queued sub-object is invisible through the cache and an
   * offline reader would be shown a stale list.
   */
  async getQueuedDeliverables(
    requestId: string,
  ): Promise<RequestDeliverable[] | null> {
    try {
      if (requestId.startsWith('o-')) {
        const offlineRequest = await this.getOfflineRequestById(requestId);
        return offlineRequest?.deliverables ?? null;
      }

      const queue = await this.getOfflineUpdateQueue();
      const pending = queue.find(
        update => update.requestId === requestId && update.status !== 'failed',
      );
      return pending?.deliverables ?? null;
    } catch (error) {
      console.error('Error reading queued deliverables:', error);
      return null;
    }
  }

  /**
   * The base a queued deliverables write was made against, or `null` when there is none.
   *
   * Separate from `getQueuedDeliverables` because the two answer different questions and a
   * caller almost always wants exactly one of them: the queued list is what to show and what
   * to send as `deliverables`, this is what to send as `base`. Conflating them is the bug
   * this exists to prevent — the queued list contains the user's removal, so offering it as
   * proof would ask the server to authorise that removal on the strength of the removal.
   *
   * `null` for an `o-*` request (nothing on the server to have seen) and for an entry queued
   * before this field existed. Callers read `null` as an empty proof.
   */
  async getQueuedDeliverablesBase(
    requestId: string,
  ): Promise<RequestDeliverable[] | null> {
    try {
      if (requestId.startsWith('o-')) return null;

      const queue = await this.getOfflineUpdateQueue();
      const pending = queue.find(
        update => update.requestId === requestId && update.status !== 'failed',
      );
      return pending?.deliverablesBase ?? null;
    } catch (error) {
      console.error('Error reading queued deliverables base:', error);
      return null;
    }
  }

  // User Profile Offline Support
  async cacheUserProfile(profile: Partial<UserProfile>): Promise<void> {
    try {
      await storage.setJSON(STORAGE_KEYS.CACHED_PROFILE, profile);
      console.log('Cached user profile offline');
    } catch (error) {
      console.error('Error caching user profile:', error);
      throw error;
    }
  }

  async getCachedUserProfile(): Promise<Partial<UserProfile> | null> {
    try {
      const cached = await storage.getJSON<Partial<UserProfile>>(
        STORAGE_KEYS.CACHED_PROFILE,
      );
      return cached || null;
    } catch (error) {
      console.error('Error getting cached user profile:', error);
      return null;
    }
  }

  // Field Permission Matrix Offline Support (keyed per stage — one matrix per canonical stage)
  async cacheFieldPermissionMatrix(
    stage: string,
    matrix: FieldPermissionMatrix,
  ): Promise<void> {
    try {
      await storage.setJSON(
        `${STORAGE_KEYS.CACHED_FIELD_PERMISSIONS}_${stage}`,
        matrix,
      );
    } catch (error) {
      // Do NOT rethrow — a cache-write failure must never fail an otherwise-successful fetch
      // that's about to be returned to the caller.
      console.error(
        `Error caching field permission matrix for stage ${stage}:`,
        error,
      );
    }
  }

  async getCachedFieldPermissionMatrix(
    stage: string,
  ): Promise<FieldPermissionMatrix | null> {
    try {
      const cached = await storage.getJSON<FieldPermissionMatrix>(
        `${STORAGE_KEYS.CACHED_FIELD_PERMISSIONS}_${stage}`,
      );
      return cached || null;
    } catch (error) {
      console.error(
        `Error getting cached field permission matrix for stage ${stage}:`,
        error,
      );
      return null;
    }
  }

  // Role Permissions Offline Support (single blob, no stage dimension — unlike field permissions)
  async cacheRolePermissions(map: Record<string, string[]>): Promise<void> {
    try {
      await storage.setJSON(STORAGE_KEYS.CACHED_ROLE_PERMISSIONS, map);
    } catch (error) {
      // Do NOT rethrow — same convention as cacheFieldPermissionMatrix: a cache-write failure
      // must never fail an otherwise-successful fetch.
      console.error('Error caching role permissions:', error);
    }
  }

  async getCachedRolePermissions(): Promise<Record<string, string[]> | null> {
    try {
      const cached = await storage.getJSON<Record<string, string[]>>(
        STORAGE_KEYS.CACHED_ROLE_PERMISSIONS,
      );
      return cached || null;
    } catch (error) {
      console.error('Error getting cached role permissions:', error);
      return null;
    }
  }

  async queueProfileUpdate(data: Partial<UserProfile>): Promise<void> {
    try {
      // Last-write-wins: overwrite any pending profile update
      await storage.setJSON(STORAGE_KEYS.OFFLINE_PROFILE_QUEUE, {
        data,
        timestamp: Date.now(),
      });
      console.log('Queued profile update for offline sync');
    } catch (error) {
      console.error('Error queuing profile update:', error);
      throw error;
    }
  }

  async getPendingProfileUpdate(): Promise<{
    data: Partial<UserProfile>;
    timestamp: number;
  } | null> {
    try {
      const queued = await storage.getJSON<{
        data: Partial<UserProfile>;
        timestamp: number;
      }>(STORAGE_KEYS.OFFLINE_PROFILE_QUEUE);
      return queued || null;
    } catch (error) {
      console.error('Error getting pending profile update:', error);
      return null;
    }
  }

  async removePendingProfileUpdate(): Promise<void> {
    try {
      await storage.removeItem(STORAGE_KEYS.OFFLINE_PROFILE_QUEUE);
    } catch (error) {
      console.error('Error removing pending profile update:', error);
    }
  }

  // Offline Rack Queue Management (#995)
  /**
   * Add a rack operation to the offline queue
   * Used when performing CRUD operations while offline
   */
  /** #1501: open the ambient offline save-group window (see module note). */
  beginOfflineSaveGroup(): string {
    currentOfflineSaveGroupId = `sg_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    return currentOfflineSaveGroupId;
  }

  /** #1501: close the window — call from the save orchestrator's finally. */
  endOfflineSaveGroup(): void {
    currentOfflineSaveGroupId = null;
  }

  async addRackToOfflineQueue(
    requestId: string,
    operation: OfflineRackOperation['operation'],
    data?: Partial<RackConfiguration>,
    rackId?: number,
    rackIds?: number[],
  ): Promise<string> {
    try {
      const operationId = `rack_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflineRackOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (rackId !== undefined) {
        offlineOp.rackId = rackId;
      }
      if (rackIds !== undefined) {
        offlineOp.rackIds = rackIds;
      }

      const queue = await this.getOfflineRackQueue();
      queue.push(offlineOp);

      await storage.setJSON(STORAGE_KEYS.OFFLINE_RACK_QUEUE, queue);

      console.log(
        `Added rack operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding rack to offline queue:', error);
      throw new Error('Failed to queue offline rack operation');
    }
  }

  /**
   * Get all rack operations from the offline queue
   */
  async getOfflineRackQueue(): Promise<OfflineRackOperation[]> {
    try {
      const queue = await storage.getJSON<OfflineRackOperation[]>(
        STORAGE_KEYS.OFFLINE_RACK_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline rack queue:', error);
      return [];
    }
  }

  /**
   * Remove a rack operation from the offline queue
   */
  async removeFromOfflineRackQueue(operationId: string): Promise<void> {
    try {
      const queue = await this.getOfflineRackQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(STORAGE_KEYS.OFFLINE_RACK_QUEUE, filteredQueue);

      console.log(`Removed rack operation from offline queue: ${operationId}`);
    } catch (error) {
      console.error('Error removing from offline rack queue:', error);
    }
  }

  /**
   * Update the rack configurations in a cached request
   * Used to keep the cache consistent with offline operations
   * Handles both regular cached requests and offline queue entries (o-* IDs)
   */
  async updateCachedRequestRacks(
    requestId: string,
    racks: RackConfiguration[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs)
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          // Store rack configurations in the offline request data
          (
            queue[requestIndex].data as unknown as {
              rackConfigurations: RackConfiguration[];
            }
          ).rackConfigurations = racks;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${racks.length} rack configurations`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update racks`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex].rackConfigurations = racks;
        await this.cacheRequests(requests);
        console.log(
          `Updated cached request ${requestId} with ${racks.length} rack configurations`,
        );
      }
    } catch (error) {
      console.error('Error updating cached request racks:', error);
    }
  }

  /**
   * Merge #1154 repeatable instance arrays onto a cached or offline-queued request.
   *
   * TEMPORARY bridge for Facility Power feeds (no server child table yet).
   * Same-device Save/Submit/reopen only — not authoritative for sync/PDF/AI.
   */
  async updateCachedRequestRepeatableConfigurations(
    requestId: string,
    configurations: Partial<{
      facilityPowerInstances: unknown[];
      rackConfigurations: unknown[];
      smallUpsConfigurations: unknown[];
      coolingSystemConfigurations: unknown[];
      liquidCoolingCduConfigurations: unknown[];
    }>,
  ): Promise<void> {
    try {
      const applyPatch = (target: Record<string, unknown>) => {
        for (const [key, value] of Object.entries(configurations)) {
          if (Array.isArray(value)) {
            target[key] = value;
          }
        }
      };

      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          applyPatch(queue[requestIndex].data as Record<string, unknown>);
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
        }
        return;
      }

      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        applyPatch(
          requests[requestIndex] as unknown as Record<string, unknown>,
        );
        await this.cacheRequests(requests);
      }
    } catch (error) {
      console.error('Error updating cached repeatable instances:', error);
      throw error;
    }
  }

  /**
   * Get pending rack operations (status === 'pending')
   */
  async getPendingRackOperations(): Promise<OfflineRackOperation[]> {
    const queue = await this.getOfflineRackQueue();
    return queue.filter(op => op.status === 'pending');
  }

  /**
   * Get failed rack operations (status === 'failed')
   */
  async getFailedRackOperations(): Promise<OfflineRackOperation[]> {
    const queue = await this.getOfflineRackQueue();
    return queue.filter(op => op.status === 'failed');
  }

  /**
   * Update the status of a rack operation
   */
  async updateRackOperationStatus(
    operationId: string,
    status: OfflineRackOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineRackQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(STORAGE_KEYS.OFFLINE_RACK_QUEUE, queue);
      }
    } catch (error) {
      console.error('Error updating rack operation status:', error);
    }
  }

  /**
   * Remap requestId in queued rack operations after an offline request syncs to a real ID
   * Called when an offline request (o-*) is successfully synced and gets a server-assigned ID
   * @param oldRequestId - The temporary offline ID (e.g., 'o-1001')
   * @param newRequestId - The real server ID (e.g., '5001')
   * @returns The number of operations that were remapped
   */
  async remapRackQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineRackQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(STORAGE_KEYS.OFFLINE_RACK_QUEUE, updatedQueue);
        console.log(
          `Remapped ${remappedCount} rack queue entries from ${oldRequestId} to ${newRequestId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping rack queue request IDs:', error);
      return 0;
    }
  }

  /**
   * Remap temporary rack IDs to server-assigned IDs in queued operations
   * Called after a rack create operation syncs and returns a real ID
   * @param requestId - The request ID these racks belong to
   * @param tempRackId - The temporary rack ID (used for local operations)
   * @param serverRackId - The real server-assigned rack ID
   * @returns The number of operations that were remapped
   */
  async remapRackIds(
    requestId: string,
    tempRackId: number,
    serverRackId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineRackQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId) {
          // Remap rackId for update/delete operations
          if (op.rackId === tempRackId) {
            remappedCount++;
            return { ...op, rackId: serverRackId };
          }
          // Remap rackIds array for reorder operations
          if (op.rackIds && op.rackIds.includes(tempRackId)) {
            remappedCount++;
            return {
              ...op,
              rackIds: op.rackIds.map(id =>
                id === tempRackId ? serverRackId : id,
              ),
            };
          }
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(STORAGE_KEYS.OFFLINE_RACK_QUEUE, updatedQueue);
        console.log(
          `Remapped ${remappedCount} rack queue entries from tempRackId ${tempRackId} to serverRackId ${serverRackId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping rack IDs:', error);
      return 0;
    }
  }

  // Offline Small UPS Queue Management (#996)
  /**
   * Add a small UPS operation to the offline queue
   * Used when performing CRUD operations while offline
   */
  async addSmallUpsToOfflineQueue(
    requestId: string,
    operation: OfflineSmallUpsOperation['operation'],
    data?: Partial<SmallUpsConfiguration>,
    smallUpsId?: number,
    smallUpsIds?: number[],
  ): Promise<string> {
    try {
      const operationId = `small_ups_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflineSmallUpsOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (smallUpsId !== undefined) {
        offlineOp.smallUpsId = smallUpsId;
      }
      if (smallUpsIds !== undefined) {
        offlineOp.smallUpsIds = smallUpsIds;
      }

      const queue = await this.getOfflineSmallUpsQueue();
      queue.push(offlineOp);

      await storage.setJSON(STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE, queue);

      console.log(
        `Added small UPS operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding small UPS to offline queue:', error);
      throw new Error('Failed to queue offline small UPS operation');
    }
  }

  /**
   * Get all small UPS operations from the offline queue
   */
  async getOfflineSmallUpsQueue(): Promise<OfflineSmallUpsOperation[]> {
    try {
      const queue = await storage.getJSON<OfflineSmallUpsOperation[]>(
        STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline small UPS queue:', error);
      return [];
    }
  }

  /**
   * Remove a small UPS operation from the offline queue
   */
  async removeFromOfflineSmallUpsQueue(operationId: string): Promise<void> {
    try {
      const queue = await this.getOfflineSmallUpsQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE,
        filteredQueue,
      );

      console.log(
        `Removed small UPS operation from offline queue: ${operationId}`,
      );
    } catch (error) {
      console.error('Error removing from offline small UPS queue:', error);
    }
  }

  /**
   * Update the small UPS configurations in a cached request
   * Used to keep the cache consistent with offline operations
   * Handles both regular cached requests and offline queue entries (o-* IDs)
   */
  async updateCachedRequestSmallUps(
    requestId: string,
    smallUps: SmallUpsConfiguration[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs)
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          // Store small UPS configurations in the offline request data
          (
            queue[requestIndex].data as unknown as {
              smallUpsConfigurations: SmallUpsConfiguration[];
            }
          ).smallUpsConfigurations = smallUps;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${smallUps.length} small UPS configurations`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update small UPS`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex].smallUpsConfigurations = smallUps;
        await this.cacheRequests(requests);
        console.log(
          `Updated cached request ${requestId} with ${smallUps.length} small UPS configurations`,
        );
      }
    } catch (error) {
      console.error('Error updating cached request small UPS:', error);
    }
  }

  /**
   * Get pending small UPS operations (status === 'pending')
   */
  async getPendingSmallUpsOperations(): Promise<OfflineSmallUpsOperation[]> {
    const queue = await this.getOfflineSmallUpsQueue();
    return queue.filter(op => op.status === 'pending');
  }

  /**
   * Get failed small UPS operations (status === 'failed')
   */
  async getFailedSmallUpsOperations(): Promise<OfflineSmallUpsOperation[]> {
    const queue = await this.getOfflineSmallUpsQueue();
    return queue.filter(op => op.status === 'failed');
  }

  /**
   * Update the status of a small UPS operation
   */
  async updateSmallUpsOperationStatus(
    operationId: string,
    status: OfflineSmallUpsOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineSmallUpsQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE, queue);
      }
    } catch (error) {
      console.error('Error updating small UPS operation status:', error);
    }
  }

  /**
   * Remap requestId in queued small UPS operations after an offline request syncs to a real ID
   * Called when an offline request (o-*) is successfully synced and gets a server-assigned ID
   * @param oldRequestId - The temporary offline ID (e.g., 'o-1001')
   * @param newRequestId - The real server ID (e.g., '5001')
   * @returns The number of operations that were remapped
   */
  async remapSmallUpsQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineSmallUpsQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} small UPS queue entries from ${oldRequestId} to ${newRequestId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping small UPS queue request IDs:', error);
      return 0;
    }
  }

  /**
   * Remap temporary small UPS IDs to server-assigned IDs in queued operations
   * Called after a small UPS create operation syncs and returns a real ID
   * @param requestId - The request ID these units belong to
   * @param tempSmallUpsId - The temporary small UPS ID (used for local operations)
   * @param serverSmallUpsId - The real server-assigned small UPS ID
   * @returns The number of operations that were remapped
   */
  async remapSmallUpsIds(
    requestId: string,
    tempSmallUpsId: number,
    serverSmallUpsId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineSmallUpsQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId) {
          // Remap smallUpsId for update/delete operations
          if (op.smallUpsId === tempSmallUpsId) {
            remappedCount++;
            return { ...op, smallUpsId: serverSmallUpsId };
          }
          // Remap smallUpsIds array for reorder operations
          if (op.smallUpsIds && op.smallUpsIds.includes(tempSmallUpsId)) {
            remappedCount++;
            return {
              ...op,
              smallUpsIds: op.smallUpsIds.map(id =>
                id === tempSmallUpsId ? serverSmallUpsId : id,
              ),
            };
          }
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} small UPS queue entries from tempSmallUpsId ${tempSmallUpsId} to serverSmallUpsId ${serverSmallUpsId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping small UPS IDs:', error);
      return 0;
    }
  }

  /**
   * Add a cooling system operation to the offline queue
   * Used when performing CRUD operations while offline
   */
  async addCoolingSystemToOfflineQueue(
    requestId: string,
    operation: OfflineCoolingSystemOperation['operation'],
    data?: Partial<CoolingSystemConfiguration>,
    coolingSystemId?: number,
    coolingSystemIds?: number[],
  ): Promise<string> {
    try {
      const operationId = `cooling_system_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflineCoolingSystemOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (coolingSystemId !== undefined) {
        offlineOp.coolingSystemId = coolingSystemId;
      }
      if (coolingSystemIds !== undefined) {
        offlineOp.coolingSystemIds = coolingSystemIds;
      }

      const queue = await this.getOfflineCoolingSystemQueue();
      queue.push(offlineOp);

      await storage.setJSON(STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE, queue);

      console.log(
        `Added cooling system operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding cooling system to offline queue:', error);
      throw new Error('Failed to queue offline cooling system operation');
    }
  }

  /**
   * Get all cooling system operations from the offline queue
   */
  async getOfflineCoolingSystemQueue(): Promise<
    OfflineCoolingSystemOperation[]
  > {
    try {
      const queue = await storage.getJSON<OfflineCoolingSystemOperation[]>(
        STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline cooling system queue:', error);
      return [];
    }
  }

  /**
   * Remove a cooling system operation from the offline queue
   */
  async removeFromOfflineCoolingSystemQueue(
    operationId: string,
  ): Promise<void> {
    try {
      const queue = await this.getOfflineCoolingSystemQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE,
        filteredQueue,
      );

      console.log(
        `Removed cooling system operation from offline queue: ${operationId}`,
      );
    } catch (error) {
      console.error('Error removing from offline cooling system queue:', error);
    }
  }

  /**
   * Update the cooling system configurations in a cached request
   * Used to keep the cache consistent with offline operations
   * Handles both regular cached requests and offline queue entries (o-* IDs)
   */
  async updateCachedRequestCoolingSystems(
    requestId: string,
    coolingSystems: CoolingSystemConfiguration[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs)
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          // Store cooling system configurations in the offline request data
          (
            queue[requestIndex].data as unknown as {
              coolingSystemConfigurations: CoolingSystemConfiguration[];
            }
          ).coolingSystemConfigurations = coolingSystems;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${coolingSystems.length} cooling system configurations`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update cooling systems`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex].coolingSystemConfigurations = coolingSystems;
        await this.cacheRequests(requests);
        console.log(
          `Updated cached request ${requestId} with ${coolingSystems.length} cooling system configurations`,
        );
      }
    } catch (error) {
      console.error('Error updating cached request cooling systems:', error);
    }
  }

  /**
   * Get pending cooling system operations (status === 'pending')
   */
  async getPendingCoolingSystemOperations(): Promise<
    OfflineCoolingSystemOperation[]
  > {
    const queue = await this.getOfflineCoolingSystemQueue();
    return queue.filter(op => op.status === 'pending');
  }

  /**
   * Get failed cooling system operations (status === 'failed')
   */
  async getFailedCoolingSystemOperations(): Promise<
    OfflineCoolingSystemOperation[]
  > {
    const queue = await this.getOfflineCoolingSystemQueue();
    return queue.filter(op => op.status === 'failed');
  }

  /**
   * Update the status of a cooling system operation
   */
  async updateCoolingSystemOperationStatus(
    operationId: string,
    status: OfflineCoolingSystemOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineCoolingSystemQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE, queue);
      }
    } catch (error) {
      console.error('Error updating cooling system operation status:', error);
    }
  }

  /**
   * Remap requestId in queued cooling system operations after an offline request syncs to a real ID
   * @param oldRequestId - The temporary offline ID (e.g., 'o-1001')
   * @param newRequestId - The real server ID (e.g., '5001')
   * @returns The number of operations that were remapped
   */
  async remapCoolingSystemQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineCoolingSystemQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} cooling system queue entries from ${oldRequestId} to ${newRequestId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping cooling system queue request IDs:', error);
      return 0;
    }
  }

  /**
   * Remap temporary cooling system IDs to server-assigned IDs in queued operations
   * @param requestId - The request ID these units belong to
   * @param tempCoolingSystemId - The temporary cooling system ID (used for local operations)
   * @param serverCoolingSystemId - The real server-assigned cooling system ID
   * @returns The number of operations that were remapped
   */
  async remapCoolingSystemIds(
    requestId: string,
    tempCoolingSystemId: number,
    serverCoolingSystemId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineCoolingSystemQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId) {
          // Remap coolingSystemId for update/delete operations
          if (op.coolingSystemId === tempCoolingSystemId) {
            remappedCount++;
            return { ...op, coolingSystemId: serverCoolingSystemId };
          }
          // Remap coolingSystemIds array for reorder operations
          if (
            op.coolingSystemIds &&
            op.coolingSystemIds.includes(tempCoolingSystemId)
          ) {
            remappedCount++;
            return {
              ...op,
              coolingSystemIds: op.coolingSystemIds.map(id =>
                id === tempCoolingSystemId ? serverCoolingSystemId : id,
              ),
            };
          }
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} cooling system queue entries from tempCoolingSystemId ${tempCoolingSystemId} to serverCoolingSystemId ${serverCoolingSystemId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping cooling system IDs:', error);
      return 0;
    }
  }

  // =========================================================================
  // Liquid Cooling CDU offline queue (#996 Phase 3) — mirrors cooling system.
  // =========================================================================

  /**
   * Add a liquid cooling CDU operation to the offline queue
   * Used when performing CRUD operations while offline
   */
  async addLiquidCoolingCduToOfflineQueue(
    requestId: string,
    operation: OfflineLiquidCoolingCduOperation['operation'],
    data?: Partial<LiquidCoolingCduConfiguration>,
    liquidCoolingCduId?: number,
    liquidCoolingCduIds?: number[],
  ): Promise<string> {
    try {
      const operationId = `liquid_cooling_cdu_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflineLiquidCoolingCduOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (liquidCoolingCduId !== undefined) {
        offlineOp.liquidCoolingCduId = liquidCoolingCduId;
      }
      if (liquidCoolingCduIds !== undefined) {
        offlineOp.liquidCoolingCduIds = liquidCoolingCduIds;
      }

      const queue = await this.getOfflineLiquidCoolingCduQueue();
      queue.push(offlineOp);

      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
        queue,
      );

      console.log(
        `Added liquid cooling CDU operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding liquid cooling CDU to offline queue:', error);
      throw new Error('Failed to queue offline liquid cooling CDU operation');
    }
  }

  /**
   * Get all liquid cooling CDU operations from the offline queue
   */
  async getOfflineLiquidCoolingCduQueue(): Promise<
    OfflineLiquidCoolingCduOperation[]
  > {
    try {
      const queue = await storage.getJSON<OfflineLiquidCoolingCduOperation[]>(
        STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline liquid cooling CDU queue:', error);
      return [];
    }
  }

  /**
   * Remove a liquid cooling CDU operation from the offline queue
   */
  async removeFromOfflineLiquidCoolingCduQueue(
    operationId: string,
  ): Promise<void> {
    try {
      const queue = await this.getOfflineLiquidCoolingCduQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
        filteredQueue,
      );

      console.log(
        `Removed liquid cooling CDU operation from offline queue: ${operationId}`,
      );
    } catch (error) {
      console.error(
        'Error removing from offline liquid cooling CDU queue:',
        error,
      );
    }
  }

  /**
   * Update the liquid cooling CDU configurations in a cached request
   * Used to keep the cache consistent with offline operations
   * Handles both regular cached requests and offline queue entries (o-* IDs)
   */
  async updateCachedRequestLiquidCoolingCdus(
    requestId: string,
    liquidCoolingCdus: LiquidCoolingCduConfiguration[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs)
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          // Store liquid cooling CDU configurations in the offline request data
          (
            queue[requestIndex].data as unknown as {
              liquidCoolingCduConfigurations: LiquidCoolingCduConfiguration[];
            }
          ).liquidCoolingCduConfigurations = liquidCoolingCdus;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${liquidCoolingCdus.length} liquid cooling CDU configurations`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update liquid cooling CDUs`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex].liquidCoolingCduConfigurations =
          liquidCoolingCdus;
        await this.cacheRequests(requests);
        console.log(
          `Updated cached request ${requestId} with ${liquidCoolingCdus.length} liquid cooling CDU configurations`,
        );
      }
    } catch (error) {
      console.error(
        'Error updating cached request liquid cooling CDUs:',
        error,
      );
    }
  }

  /**
   * Get pending liquid cooling CDU operations (status === 'pending')
   */
  async getPendingLiquidCoolingCduOperations(): Promise<
    OfflineLiquidCoolingCduOperation[]
  > {
    const queue = await this.getOfflineLiquidCoolingCduQueue();
    return queue.filter(op => op.status === 'pending');
  }

  /**
   * Get failed liquid cooling CDU operations (status === 'failed')
   */
  async getFailedLiquidCoolingCduOperations(): Promise<
    OfflineLiquidCoolingCduOperation[]
  > {
    const queue = await this.getOfflineLiquidCoolingCduQueue();
    return queue.filter(op => op.status === 'failed');
  }

  /**
   * Update the status of a liquid cooling CDU operation
   */
  async updateLiquidCoolingCduOperationStatus(
    operationId: string,
    status: OfflineLiquidCoolingCduOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineLiquidCoolingCduQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
          queue,
        );
      }
    } catch (error) {
      console.error(
        'Error updating liquid cooling CDU operation status:',
        error,
      );
    }
  }

  /**
   * Remap requestId in queued liquid cooling CDU operations after an offline request syncs to a real ID
   * @param oldRequestId - The temporary offline ID (e.g., 'o-1001')
   * @param newRequestId - The real server ID (e.g., '5001')
   * @returns The number of operations that were remapped
   */
  async remapLiquidCoolingCduQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineLiquidCoolingCduQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} liquid cooling CDU queue entries from ${oldRequestId} to ${newRequestId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error(
        'Error remapping liquid cooling CDU queue request IDs:',
        error,
      );
      return 0;
    }
  }

  /**
   * Remap temporary liquid cooling CDU IDs to server-assigned IDs in queued operations
   * @param requestId - The request ID these units belong to
   * @param tempLiquidCoolingCduId - The temporary CDU ID (used for local operations)
   * @param serverLiquidCoolingCduId - The real server-assigned CDU ID
   * @returns The number of operations that were remapped
   */
  async remapLiquidCoolingCduIds(
    requestId: string,
    tempLiquidCoolingCduId: number,
    serverLiquidCoolingCduId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineLiquidCoolingCduQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId) {
          // Remap liquidCoolingCduId for update/delete operations
          if (op.liquidCoolingCduId === tempLiquidCoolingCduId) {
            remappedCount++;
            return { ...op, liquidCoolingCduId: serverLiquidCoolingCduId };
          }
          // Remap liquidCoolingCduIds array for reorder operations
          if (
            op.liquidCoolingCduIds &&
            op.liquidCoolingCduIds.includes(tempLiquidCoolingCduId)
          ) {
            remappedCount++;
            return {
              ...op,
              liquidCoolingCduIds: op.liquidCoolingCduIds.map(id =>
                id === tempLiquidCoolingCduId ? serverLiquidCoolingCduId : id,
              ),
            };
          }
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} liquid cooling CDU queue entries from tempLiquidCoolingCduId ${tempLiquidCoolingCduId} to serverLiquidCoolingCduId ${serverLiquidCoolingCduId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping liquid cooling CDU IDs:', error);
      return 0;
    }
  }

  // =========================================================================
  // Large UPS offline queue (#996 Phase 4) — mirrors cooling system.
  // =========================================================================

  /**
   * Add a large UPS operation to the offline queue
   * Used when performing CRUD operations while offline
   */
  async addLargeUpsToOfflineQueue(
    requestId: string,
    operation: OfflineLargeUpsOperation['operation'],
    data?: Partial<LargeUpsConfiguration>,
    largeUpsId?: number,
    largeUpsIds?: number[],
  ): Promise<string> {
    try {
      const operationId = `large_ups_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflineLargeUpsOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (largeUpsId !== undefined) {
        offlineOp.largeUpsId = largeUpsId;
      }
      if (largeUpsIds !== undefined) {
        offlineOp.largeUpsIds = largeUpsIds;
      }

      const queue = await this.getOfflineLargeUpsQueue();
      queue.push(offlineOp);

      await storage.setJSON(STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE, queue);

      console.log(
        `Added large UPS operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding large UPS to offline queue:', error);
      throw new Error('Failed to queue offline large UPS operation');
    }
  }

  /**
   * Get all large UPS operations from the offline queue
   */
  async getOfflineLargeUpsQueue(): Promise<OfflineLargeUpsOperation[]> {
    try {
      const queue = await storage.getJSON<OfflineLargeUpsOperation[]>(
        STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline large UPS queue:', error);
      return [];
    }
  }

  /**
   * Remove a large UPS operation from the offline queue
   */
  async removeFromOfflineLargeUpsQueue(operationId: string): Promise<void> {
    try {
      const queue = await this.getOfflineLargeUpsQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE,
        filteredQueue,
      );

      console.log(
        `Removed large UPS operation from offline queue: ${operationId}`,
      );
    } catch (error) {
      console.error('Error removing from offline large UPS queue:', error);
    }
  }

  /**
   * Update the large UPS configurations in a cached request
   * Used to keep the cache consistent with offline operations
   * Handles both regular cached requests and offline queue entries (o-* IDs)
   */
  async updateCachedRequestLargeUps(
    requestId: string,
    largeUps: LargeUpsConfiguration[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs)
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          // Store large UPS configurations in the offline request data
          (
            queue[requestIndex].data as unknown as {
              largeUpsConfigurations: LargeUpsConfiguration[];
            }
          ).largeUpsConfigurations = largeUps;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${largeUps.length} large UPS configurations`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update large UPSs`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex].largeUpsConfigurations = largeUps;
        await this.cacheRequests(requests);
        console.log(
          `Updated cached request ${requestId} with ${largeUps.length} large UPS configurations`,
        );
      }
    } catch (error) {
      console.error('Error updating cached request large UPSs:', error);
    }
  }

  /**
   * Get pending large UPS operations (status === 'pending')
   */
  async getPendingLargeUpsOperations(): Promise<OfflineLargeUpsOperation[]> {
    const queue = await this.getOfflineLargeUpsQueue();
    return queue.filter(op => op.status === 'pending');
  }

  /**
   * Get failed large UPS operations (status === 'failed')
   */
  async getFailedLargeUpsOperations(): Promise<OfflineLargeUpsOperation[]> {
    const queue = await this.getOfflineLargeUpsQueue();
    return queue.filter(op => op.status === 'failed');
  }

  /**
   * Update the status of a large UPS operation
   */
  async updateLargeUpsOperationStatus(
    operationId: string,
    status: OfflineLargeUpsOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineLargeUpsQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE, queue);
      }
    } catch (error) {
      console.error('Error updating large UPS operation status:', error);
    }
  }

  /**
   * Remap requestId in queued large UPS operations after an offline request syncs to a real ID
   * @param oldRequestId - The temporary offline ID (e.g., 'o-1001')
   * @param newRequestId - The real server ID (e.g., '5001')
   * @returns The number of operations that were remapped
   */
  async remapLargeUpsQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineLargeUpsQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} large UPS queue entries from ${oldRequestId} to ${newRequestId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping large UPS queue request IDs:', error);
      return 0;
    }
  }

  /**
   * Remap temporary large UPS IDs to server-assigned IDs in queued operations
   * @param requestId - The request ID these units belong to
   * @param tempLargeUpsId - The temporary CDU ID (used for local operations)
   * @param serverLargeUpsId - The real server-assigned CDU ID
   * @returns The number of operations that were remapped
   */
  async remapLargeUpsIds(
    requestId: string,
    tempLargeUpsId: number,
    serverLargeUpsId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineLargeUpsQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId) {
          // Remap largeUpsId for update/delete operations
          if (op.largeUpsId === tempLargeUpsId) {
            remappedCount++;
            return { ...op, largeUpsId: serverLargeUpsId };
          }
          // Remap largeUpsIds array for reorder operations
          if (op.largeUpsIds && op.largeUpsIds.includes(tempLargeUpsId)) {
            remappedCount++;
            return {
              ...op,
              largeUpsIds: op.largeUpsIds.map(id =>
                id === tempLargeUpsId ? serverLargeUpsId : id,
              ),
            };
          }
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} large UPS queue entries from tempLargeUpsId ${tempLargeUpsId} to serverLargeUpsId ${serverLargeUpsId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping large UPS IDs:', error);
      return 0;
    }
  }

  /**
   * Add a power distribution operation to the offline queue
   * Used when performing CRUD operations while offline
   */
  async addPowerDistributionToOfflineQueue(
    requestId: string,
    operation: OfflinePowerDistributionOperation['operation'],
    data?: Partial<PowerDistributionConfiguration>,
    powerDistributionId?: number,
    powerDistributionIds?: number[],
  ): Promise<string> {
    try {
      const operationId = `power_distribution_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflinePowerDistributionOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (powerDistributionId !== undefined) {
        offlineOp.powerDistributionId = powerDistributionId;
      }
      if (powerDistributionIds !== undefined) {
        offlineOp.powerDistributionIds = powerDistributionIds;
      }

      const queue = await this.getOfflinePowerDistributionQueue();
      queue.push(offlineOp);

      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
        queue,
      );

      console.log(
        `Added power distribution operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding power distribution to offline queue:', error);
      throw new Error('Failed to queue offline power distribution operation');
    }
  }

  /**
   * Get all power distribution operations from the offline queue
   */
  async getOfflinePowerDistributionQueue(): Promise<
    OfflinePowerDistributionOperation[]
  > {
    try {
      const queue = await storage.getJSON<OfflinePowerDistributionOperation[]>(
        STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline power distribution queue:', error);
      return [];
    }
  }

  /**
   * Remove a power distribution operation from the offline queue
   */
  async removeFromOfflinePowerDistributionQueue(
    operationId: string,
  ): Promise<void> {
    try {
      const queue = await this.getOfflinePowerDistributionQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
        filteredQueue,
      );

      console.log(
        `Removed power distribution operation from offline queue: ${operationId}`,
      );
    } catch (error) {
      console.error(
        'Error removing from offline power distribution queue:',
        error,
      );
    }
  }

  /**
   * Update the power distribution configurations in a cached request
   * Used to keep the cache consistent with offline operations
   * Handles both regular cached requests and offline queue entries (o-* IDs)
   */
  async updateCachedRequestPowerDistribution(
    requestId: string,
    units: PowerDistributionConfiguration[],
  ): Promise<void> {
    try {
      // Handle offline-created requests (o-* IDs)
      if (requestId.startsWith('o-')) {
        const queue = await this.getOfflineQueue();
        const requestIndex = queue.findIndex(req => req.id === requestId);

        if (requestIndex !== -1) {
          // Store power distribution configurations in the offline request data
          (
            queue[requestIndex].data as unknown as {
              powerDistributionConfigurations: PowerDistributionConfiguration[];
            }
          ).powerDistributionConfigurations = units;
          await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, queue);
          console.log(
            `Updated offline request ${requestId} with ${units.length} power distribution configurations`,
          );
        } else {
          console.log(
            `Offline request ${requestId} not found in queue, cannot update power distributions`,
          );
        }
        return;
      }

      // Handle regular cached requests
      const { requests } = await this.getCachedRequests();
      const requestIndex = requests.findIndex(req => req.id === requestId);

      if (requestIndex !== -1) {
        requests[requestIndex].powerDistributionConfigurations = units;
        await this.cacheRequests(requests);
        console.log(
          `Updated cached request ${requestId} with ${units.length} power distribution configurations`,
        );
      }
    } catch (error) {
      console.error(
        'Error updating cached request power distributions:',
        error,
      );
    }
  }

  /**
   * Get pending power distribution operations (status === 'pending')
   */
  async getPendingPowerDistributionOperations(): Promise<
    OfflinePowerDistributionOperation[]
  > {
    const queue = await this.getOfflinePowerDistributionQueue();
    return queue.filter(op => op.status === 'pending');
  }

  /**
   * Get failed power distribution operations (status === 'failed')
   */
  async getFailedPowerDistributionOperations(): Promise<
    OfflinePowerDistributionOperation[]
  > {
    const queue = await this.getOfflinePowerDistributionQueue();
    return queue.filter(op => op.status === 'failed');
  }

  /**
   * Update the status of a power distribution operation
   */
  async updatePowerDistributionOperationStatus(
    operationId: string,
    status: OfflinePowerDistributionOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflinePowerDistributionQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
          queue,
        );
      }
    } catch (error) {
      console.error(
        'Error updating power distribution operation status:',
        error,
      );
    }
  }

  /**
   * Remap requestId in queued power distribution operations after an offline request syncs to a real ID
   * @param oldRequestId - The temporary offline ID (e.g., 'o-1001')
   * @param newRequestId - The real server ID (e.g., '5001')
   * @returns The number of operations that were remapped
   */
  async remapPowerDistributionQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflinePowerDistributionQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} power distribution queue entries from ${oldRequestId} to ${newRequestId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error(
        'Error remapping power distribution queue request IDs:',
        error,
      );
      return 0;
    }
  }

  /**
   * Remap temporary power distribution IDs to server-assigned IDs in queued operations
   * @param requestId - The request ID these units belong to
   * @param tempPowerDistributionId - The temporary CDU ID (used for local operations)
   * @param serverPowerDistributionId - The real server-assigned CDU ID
   * @returns The number of operations that were remapped
   */
  async remapPowerDistributionIds(
    requestId: string,
    tempPowerDistributionId: number,
    serverPowerDistributionId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflinePowerDistributionQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId) {
          // Remap powerDistributionId for update/delete operations
          if (op.powerDistributionId === tempPowerDistributionId) {
            remappedCount++;
            return { ...op, powerDistributionId: serverPowerDistributionId };
          }
          // Remap powerDistributionIds array for reorder operations
          if (
            op.powerDistributionIds &&
            op.powerDistributionIds.includes(tempPowerDistributionId)
          ) {
            remappedCount++;
            return {
              ...op,
              powerDistributionIds: op.powerDistributionIds.map(id =>
                id === tempPowerDistributionId ? serverPowerDistributionId : id,
              ),
            };
          }
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
          updatedQueue,
        );
        console.log(
          `Remapped ${remappedCount} power distribution queue entries from tempPowerDistributionId ${tempPowerDistributionId} to serverPowerDistributionId ${serverPowerDistributionId}`,
        );
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping power distribution IDs:', error);
      return 0;
    }
  }

  // Offline Location Tag Queue Management (#999)
  async getCachedLocationTags(requestId: string): Promise<LocationTag[]> {
    try {
      const map = await storage.getJSON<Record<string, LocationTag[]>>(
        STORAGE_KEYS.CACHED_LOCATION_TAGS,
      );
      return map?.[requestId] ?? [];
    } catch (error) {
      console.error('Error getting cached location tags:', error);
      return [];
    }
  }

  async setCachedLocationTags(
    requestId: string,
    tags: LocationTag[],
  ): Promise<void> {
    try {
      const map =
        (await storage.getJSON<Record<string, LocationTag[]>>(
          STORAGE_KEYS.CACHED_LOCATION_TAGS,
        )) ?? {};
      map[requestId] = tags;
      await storage.setJSON(STORAGE_KEYS.CACHED_LOCATION_TAGS, map);
    } catch (error) {
      console.error('Error setting cached location tags:', error);
      throw new Error('Failed to cache location tags');
    }
  }

  async addLocationTagToOfflineQueue(
    requestId: string,
    operation: OfflineLocationTagOperation['operation'],
    data?: { name: string },
    tagId?: number,
  ): Promise<string> {
    try {
      const operationId = `location_tag_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const offlineOp: OfflineLocationTagOperation = {
        id: operationId,
        requestId,
        operation,
        timestamp: Date.now(),
        status: 'pending',
        ...currentSaveGroupStamp(),
      };

      if (data !== undefined) {
        offlineOp.data = data;
      }
      if (tagId !== undefined) {
        offlineOp.tagId = tagId;
      }

      const queue = await this.getOfflineLocationTagQueue();
      queue.push(offlineOp);
      await storage.setJSON(STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE, queue);

      console.log(
        `Added location tag operation to offline queue: ${operationId} (${operation})`,
      );
      return operationId;
    } catch (error) {
      console.error('Error adding location tag to offline queue:', error);
      throw new Error('Failed to queue offline location tag operation');
    }
  }

  async getOfflineLocationTagQueue(): Promise<OfflineLocationTagOperation[]> {
    try {
      const queue = await storage.getJSON<OfflineLocationTagOperation[]>(
        STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline location tag queue:', error);
      return [];
    }
  }

  async removeFromOfflineLocationTagQueue(operationId: string): Promise<void> {
    try {
      const queue = await this.getOfflineLocationTagQueue();
      const filteredQueue = queue.filter(op => op.id !== operationId);
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE,
        filteredQueue,
      );
    } catch (error) {
      console.error('Error removing from offline location tag queue:', error);
    }
  }

  async getPendingLocationTagOperations(): Promise<
    OfflineLocationTagOperation[]
  > {
    const queue = await this.getOfflineLocationTagQueue();
    return queue.filter(op => op.status === 'pending');
  }

  async renamePendingLocationTagCreate(
    requestId: string,
    tempTagId: number,
    name: string,
  ): Promise<boolean> {
    const queue = await this.getOfflineLocationTagQueue();
    const index = queue.findIndex(
      op =>
        op.status === 'pending' &&
        op.operation === 'create' &&
        String(op.requestId) === String(requestId) &&
        op.tagId === tempTagId,
    );
    if (index < 0) return false;
    queue[index] = { ...queue[index], data: { name } };
    await storage.setJSON(STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE, queue);
    return true;
  }

  async removePendingLocationTagCreate(
    requestId: string,
    tempTagId: number,
  ): Promise<boolean> {
    const queue = await this.getOfflineLocationTagQueue();
    const filtered = queue.filter(
      op =>
        !(
          op.status === 'pending' &&
          op.operation === 'create' &&
          String(op.requestId) === String(requestId) &&
          op.tagId === tempTagId
        ),
    );
    if (filtered.length === queue.length) return false;
    await storage.setJSON(STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE, filtered);
    return true;
  }

  async isLocationTagAssigned(
    requestId: string,
    tagId: number,
  ): Promise<boolean> {
    const request = await this.getCachedRequestById(requestId);
    const repeatableKeys = [
      'rackConfigurations',
      'smallUpsConfigurations',
      'coolingSystemConfigurations',
      'liquidCoolingCduConfigurations',
      'largeUpsConfigurations',
      'powerDistributionConfigurations',
    ] as const;
    const record = request as unknown as Record<string, unknown> | null;
    if (
      record &&
      repeatableKeys.some(key => {
        const units = record[key];
        return (
          Array.isArray(units) &&
          (units as Array<{ locationTagId?: number | null }>).some(
            unit => unit.locationTagId === tagId,
          )
        );
      })
    ) {
      return true;
    }

    const queues = await Promise.all([
      this.getOfflineRackQueue(),
      this.getOfflineSmallUpsQueue(),
      this.getOfflineCoolingSystemQueue(),
      this.getOfflineLiquidCoolingCduQueue(),
      this.getOfflineLargeUpsQueue(),
      this.getOfflinePowerDistributionQueue(),
    ]);
    return queues.some(queue =>
      queue.some(
        op =>
          op.requestId === requestId &&
          op.operation !== 'delete' &&
          op.data?.locationTagId === tagId,
      ),
    );
  }

  async updateLocationTagOperationStatus(
    operationId: string,
    status: OfflineLocationTagOperation['status'],
  ): Promise<void> {
    try {
      const queue = await this.getOfflineLocationTagQueue();
      const opIndex = queue.findIndex(op => op.id === operationId);

      if (opIndex !== -1) {
        queue[opIndex].status = status;
        await storage.setJSON(STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE, queue);
      }
    } catch (error) {
      console.error('Error updating location tag operation status:', error);
    }
  }

  async remapLocationTagQueueRequestIds(
    oldRequestId: string,
    newRequestId: string,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineLocationTagQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === oldRequestId) {
          remappedCount++;
          return { ...op, requestId: newRequestId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE,
          updatedQueue,
        );
      }

      const tagMap = await storage.getJSON<Record<string, LocationTag[]>>(
        STORAGE_KEYS.CACHED_LOCATION_TAGS,
      );
      if (tagMap?.[oldRequestId]) {
        tagMap[newRequestId] = tagMap[oldRequestId];
        delete tagMap[oldRequestId];
        await storage.setJSON(STORAGE_KEYS.CACHED_LOCATION_TAGS, tagMap);
      }

      return remappedCount;
    } catch (error) {
      console.error('Error remapping location tag queue request IDs:', error);
      return 0;
    }
  }

  async remapLocationTagIds(
    requestId: string,
    tempTagId: number,
    serverTagId: number,
  ): Promise<number> {
    try {
      const queue = await this.getOfflineLocationTagQueue();
      let remappedCount = 0;

      const updatedQueue = queue.map(op => {
        if (op.requestId === requestId && op.tagId === tempTagId) {
          remappedCount++;
          return { ...op, tagId: serverTagId };
        }
        return op;
      });

      if (remappedCount > 0) {
        await storage.setJSON(
          STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE,
          updatedQueue,
        );
      }

      const tags = await this.getCachedLocationTags(requestId);
      const remappedTags = tags.map(tag =>
        tag.id === tempTagId ? { ...tag, id: serverTagId } : tag,
      );
      if (remappedTags.some((tag, index) => tag.id !== tags[index]?.id)) {
        await this.setCachedLocationTags(requestId, remappedTags);
      }

      remappedCount += await this.remapUnitLocationTagAssignments(
        requestId,
        tempTagId,
        serverTagId,
      );

      return remappedCount;
    } catch (error) {
      console.error('Error remapping location tag IDs:', error);
      return 0;
    }
  }

  /**
   * After a draft location tag receives a server id, rewrite unit assignment FKs
   * in offline unit queues and cached request repeatable arrays (#999 FR35).
   */
  private async remapUnitLocationTagAssignments(
    requestId: string,
    tempTagId: number,
    serverTagId: number,
  ): Promise<number> {
    let remappedCount = 0;

    const remapQueueData = async <
      T extends { requestId: string; data?: { locationTagId?: number | null } },
    >(
      getQueue: () => Promise<T[]>,
      storageKey: string,
    ): Promise<void> => {
      const queue = await getQueue();
      let queueChanged = false;
      const updated = queue.map(op => {
        if (
          op.requestId !== requestId ||
          op.data?.locationTagId !== tempTagId
        ) {
          return op;
        }
        queueChanged = true;
        remappedCount++;
        return {
          ...op,
          data: { ...op.data, locationTagId: serverTagId },
        };
      });
      if (queueChanged) {
        await storage.setJSON(storageKey, updated);
      }
    };

    await remapQueueData(
      () => this.getOfflineRackQueue(),
      STORAGE_KEYS.OFFLINE_RACK_QUEUE,
    );
    await remapQueueData(
      () => this.getOfflineSmallUpsQueue(),
      STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE,
    );
    await remapQueueData(
      () => this.getOfflineCoolingSystemQueue(),
      STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE,
    );
    await remapQueueData(
      () => this.getOfflineLiquidCoolingCduQueue(),
      STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE,
    );
    await remapQueueData(
      () => this.getOfflineLargeUpsQueue(),
      STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE,
    );
    await remapQueueData(
      () => this.getOfflinePowerDistributionQueue(),
      STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE,
    );

    const repeatableKeys = [
      'rackConfigurations',
      'smallUpsConfigurations',
      'coolingSystemConfigurations',
      'liquidCoolingCduConfigurations',
      'largeUpsConfigurations',
      'powerDistributionConfigurations',
    ] as const;

    const remapRequestRecord = (record: Record<string, unknown>): boolean => {
      let recordChanged = false;
      for (const key of repeatableKeys) {
        const units = record[key];
        if (!Array.isArray(units)) {
          continue;
        }
        let keyChanged = false;
        const remappedUnits = units.map(unit => {
          if (
            typeof unit !== 'object' ||
            unit === null ||
            (unit as { locationTagId?: number | null }).locationTagId !==
              tempTagId
          ) {
            return unit;
          }
          keyChanged = true;
          remappedCount++;
          return { ...unit, locationTagId: serverTagId };
        });
        if (keyChanged) {
          record[key] = remappedUnits;
          recordChanged = true;
        }
      }
      return recordChanged;
    };

    const { requests } = await this.getCachedRequests();
    let requestsChanged = false;
    for (const request of requests) {
      if (String(request.id) !== String(requestId)) {
        continue;
      }
      if (remapRequestRecord(request as unknown as Record<string, unknown>)) {
        requestsChanged = true;
      }
    }
    if (requestsChanged) {
      await this.cacheRequests(requests);
    }

    const offlineCreateQueue = await this.getOfflineQueue();
    let offlineQueueChanged = false;
    for (const entry of offlineCreateQueue) {
      if (String(entry.id) !== String(requestId)) {
        continue;
      }
      if (
        remapRequestRecord(entry.data as unknown as Record<string, unknown>)
      ) {
        offlineQueueChanged = true;
      }
    }
    if (offlineQueueChanged) {
      await storage.setJSON(STORAGE_KEYS.OFFLINE_QUEUE, offlineCreateQueue);
    }

    if (remappedCount > 0) {
      console.log(
        `Remapped ${remappedCount} unit locationTagId references from temp ${tempTagId} to server ${serverTagId} for request ${requestId}`,
      );
    }

    return remappedCount;
  }

  // Read markers (#1575 story 3.2)

  /**
   * Queue "this person opened `requestId` at `readAt`" for replay (AC 2, AC 3).
   *
   * Collapses onto one entry per request: the LATEST `readAt` wins, which is not
   * always the newest call — a device whose clock stepped backwards can offer an
   * older instant second, and taking it would move the marker backwards and
   * re-bold a row the person has already seen. The existing entry keeps its `id`
   * so a sync pass already holding that id still dequeues the right row.
   *
   * Never called for an `o-` offline-create id: an unsaved draft has no server
   * request to mark read, so `useRecordRequestRead` filters those out before any
   * of this runs. That is also why this queue needs no `remap…RequestIds`
   * counterpart (contrast `remapLocationTagQueueRequestIds`).
   */
  async addReadToOfflineQueue(
    requestId: string,
    readAt: string,
  ): Promise<string> {
    try {
      const queue = await this.getOfflineReadQueue();
      const existing = queue.find(op => op.requestId === requestId);

      if (existing) {
        if (Date.parse(readAt) > Date.parse(existing.readAt)) {
          existing.readAt = readAt;
          existing.timestamp = Date.now();
          await storage.setJSON(STORAGE_KEYS.OFFLINE_READ_QUEUE, queue);
        }
        return existing.id;
      }

      const operationId = `read_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      queue.push({
        id: operationId,
        requestId,
        readAt,
        timestamp: Date.now(),
      });
      await storage.setJSON(STORAGE_KEYS.OFFLINE_READ_QUEUE, queue);

      return operationId;
    } catch (error) {
      console.error('Error adding read marker to offline queue:', error);
      throw new Error('Failed to queue offline read marker');
    }
  }

  async getOfflineReadQueue(): Promise<OfflineReadOperation[]> {
    try {
      const queue = await storage.getJSON<OfflineReadOperation[]>(
        STORAGE_KEYS.OFFLINE_READ_QUEUE,
      );
      return queue || [];
    } catch (error) {
      console.error('Error getting offline read queue:', error);
      return [];
    }
  }

  /**
   * Every queued read, in the order they were first queued.
   *
   * Named for the family's `getPending…Operations` so the sync pass reads the
   * same, but with no `status` filter — see `OfflineReadOperation` for why this
   * queue has no status to filter on.
   */
  async getPendingReadOperations(): Promise<OfflineReadOperation[]> {
    return this.getOfflineReadQueue();
  }

  async removeFromOfflineReadQueue(operationId: string): Promise<void> {
    try {
      const queue = await this.getOfflineReadQueue();
      await storage.setJSON(
        STORAGE_KEYS.OFFLINE_READ_QUEUE,
        queue.filter(op => op.id !== operationId),
      );
    } catch (error) {
      console.error('Error removing from offline read queue:', error);
    }
  }

  // Clear cache (for testing or reset)
  async clearCache(): Promise<void> {
    try {
      await Promise.all([
        storage.removeItem(STORAGE_KEYS.CACHED_REQUESTS),
        storage.removeItem(STORAGE_KEYS.OFFLINE_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_UPDATE_QUEUE),
        storage.removeItem(STORAGE_KEYS.LAST_SYNC),
        storage.removeItem(STORAGE_KEYS.OFFLINE_ID_COUNTER),
        storage.removeItem(STORAGE_KEYS.CACHED_PROFILE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_PROFILE_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_RACK_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_SMALL_UPS_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_COOLING_SYSTEM_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_LIQUID_COOLING_CDU_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_LARGE_UPS_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_POWER_DISTRIBUTION_QUEUE),
        storage.removeItem(STORAGE_KEYS.OFFLINE_LOCATION_TAG_QUEUE),
        storage.removeItem(STORAGE_KEYS.CACHED_LOCATION_TAGS),
        storage.removeItem(STORAGE_KEYS.OFFLINE_READ_QUEUE),
      ]);

      console.log('Offline cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
}

export const offlineCache = new OfflineCacheService();
